import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  CheckCircle2,
  BookOpen,
  Headphones,
  Sparkles,
  Users,
  ArrowRight,
  Mail,
  Heart,
} from 'lucide-react';

export default function BundleWelcomePage() {
  const navigate = useNavigate();
  const [claim, setClaim] = useState(null);

  useEffect(() => {
    const stored = sessionStorage.getItem('bundle-claim');
    if (!stored) {
      navigate('/bundle');
      return;
    }
    try {
      setClaim(JSON.parse(stored));
    } catch {
      navigate('/bundle');
    }
  }, [navigate]);

  if (!claim) return null;

  const first = (claim.name || '').trim().split(' ')[0];

  return (
    <div className="min-h-screen bg-background" data-testid="bundle-welcome-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-secondary/15 via-background to-primary/10" />
        <div className="absolute top-10 -right-10 w-96 h-96 rounded-full bg-primary/10 blur-3xl" />

        <div className="relative container mx-auto px-4 pt-14 pb-8 md:pt-20 max-w-4xl text-center">
          <motion.div
            initial={{ scale: 0.7, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5, type: 'spring' }}
            className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-secondary to-teal-600 mb-6 shadow-lg"
          >
            <CheckCircle2 className="w-10 h-10 text-white" />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="font-display text-4xl sm:text-5xl font-bold text-foreground mb-4"
            data-testid="bundle-welcome-heading"
          >
            {first ? `Welcome, ${first}.` : 'Welcome home.'}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-lg text-muted-foreground max-w-xl mx-auto"
          >
            Your bundle is ready. I&rsquo;ve also sent both links to <strong className="text-foreground">{claim.email}</strong> so you have them saved for later.
          </motion.p>
        </div>
      </section>

      {/* THE TWO GIFTS */}
      <section className="container mx-auto px-4 pb-8 max-w-4xl">
        <div className="grid md:grid-cols-2 gap-5">
          {/* E-BOOK */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card className="p-6 h-full border-2 border-primary/30 bg-gradient-to-br from-card to-primary/5" data-testid="bundle-gift-ebook">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-maroon to-primary flex items-center justify-center shadow-md">
                  <BookOpen className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-primary">Gift 1</p>
                  <h3 className="font-display text-xl font-bold text-foreground leading-tight">The E-Book</h3>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-5 leading-relaxed">
                Your complete guide to the Magnetic Mind Method. Download it to your device and read anywhere.
              </p>
              <a
                href={claim.ebook_download_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="bundle-download-ebook"
              >
                <Button className="w-full h-12 bg-gradient-to-r from-primary to-maroon text-primary-foreground font-semibold shadow">
                  <BookOpen className="w-4 h-4 mr-2" /> Download the E-Book (PDF)
                </Button>
              </a>
            </Card>
          </motion.div>

          {/* PRACTICE APP */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <Card className="p-6 h-full border-2 border-secondary/30 bg-gradient-to-br from-card to-secondary/5" data-testid="bundle-gift-practice">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-secondary to-teal-600 flex items-center justify-center shadow-md">
                  <Headphones className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-secondary">Gift 2</p>
                  <h3 className="font-display text-xl font-bold text-foreground leading-tight">The Practice App</h3>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-5 leading-relaxed">
                13 guided meditations + printable worksheets. Log in from any device with just your email.
              </p>
              <a
                href={claim.practice_portal_url}
                data-testid="bundle-open-practice"
              >
                <Button className="w-full h-12 bg-gradient-to-r from-secondary to-teal-600 text-white font-semibold shadow">
                  <Headphones className="w-4 h-4 mr-2" /> Open the Practice App
                </Button>
              </a>
            </Card>
          </motion.div>
        </div>

        {/* GIFT 3 — INTERACTIVE E-COURSE (full width on desktop) */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mt-5"
        >
          <Card className="p-6 border-2 border-primary/30 bg-gradient-to-br from-card via-primary/5 to-secondary/5" data-testid="bundle-gift-course">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex items-center gap-3 flex-1">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center shadow-md shrink-0">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-primary">Gift 3</p>
                  <h3 className="font-display text-xl font-bold text-foreground leading-tight">The Interactive E-Course</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mt-1">
                    Read all 5 chapters online with journaling prompts, progress tracking, and dark mode. No login required.
                  </p>
                </div>
              </div>
              <Link to="/course" data-testid="bundle-open-course" className="shrink-0">
                <Button className="w-full sm:w-auto h-12 bg-gradient-to-r from-primary to-secondary text-white font-semibold shadow">
                  <Sparkles className="w-4 h-4 mr-2" /> Open the Course
                </Button>
              </Link>
            </div>
          </Card>
        </motion.div>

        {/* Email confirmation strip */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mt-6 flex items-center justify-center gap-2 text-sm text-muted-foreground"
        >
          <Mail className="w-4 h-4" />
          Both links have also been sent to your inbox &mdash; check spam if you don&rsquo;t see it in a minute.
        </motion.div>
      </section>

      {/* UPSELL — THE POWER COLLECTIVE */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="container mx-auto px-4 py-14 max-w-4xl"
      >
        <Card className="overflow-hidden border-2 border-primary/40 bg-gradient-to-br from-maroon/10 via-card to-primary/10 shadow-2xl">
          <div className="p-8 sm:p-10">
            <div className="text-center max-w-2xl mx-auto">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/15 text-primary text-[11px] font-bold uppercase tracking-widest mb-5">
                <Sparkles className="w-3 h-3" /> One More Thing
              </div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-4 leading-tight">
                Want to go deeper with me — live?
              </h2>
              <p className="text-muted-foreground text-lg mb-8 leading-relaxed">
                The bundle gets you started. <strong className="text-foreground">The Power Collective</strong> keeps you moving — with weekly Power Hours, live coaching, and a community of others doing the work alongside you.
              </p>

              <div className="grid sm:grid-cols-3 gap-4 mb-8 text-left">
                {[
                  { icon: Users, title: 'Weekly Power Hour', detail: 'Live group coaching every week with me.' },
                  { icon: Heart, title: 'Welcome Circle', detail: 'Monthly onboarding call for new members.' },
                  { icon: Sparkles, title: 'Members-Only Library', detail: 'Deep-dive workshops and Q&A recordings.' },
                ].map((f) => {
                  const Icon = f.icon;
                  return (
                    <div key={f.title} className="flex flex-col items-start p-4 rounded-lg bg-card/60 border border-silver/20">
                      <Icon className="w-5 h-5 text-primary mb-2" />
                      <p className="text-sm font-bold text-foreground mb-0.5">{f.title}</p>
                      <p className="text-xs text-muted-foreground leading-relaxed">{f.detail}</p>
                    </div>
                  );
                })}
              </div>

              <Link to="/collective" data-testid="bundle-upsell-collective">
                <Button
                  size="lg"
                  className="h-14 px-8 text-base font-bold bg-gradient-to-r from-primary to-maroon text-primary-foreground shadow-lg"
                >
                  Explore The Power Collective <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <p className="text-xs text-muted-foreground mt-4 italic">
                No pressure. Ever. Only if it feels aligned.
              </p>
            </div>
          </div>
        </Card>
      </motion.section>

      {/* Small footer link back */}
      <div className="text-center pb-14">
        <Link
          to="/"
          className="text-sm text-muted-foreground hover:text-primary transition-colors"
          data-testid="bundle-back-home"
        >
          &larr; Back to home
        </Link>
      </div>
    </div>
  );
}
